/**
 * Окно выигрыша.
 *
 * Открывается только после остановки ленты: если показать приз раньше,
 * прокрутка теряет смысл. Кнопка повторного открытия стоит здесь же — это
 * основной путь игрока, и возвращать его к кнопке внизу экрана не нужно.
 */

import { formatPaws, type CaseOpeningDto } from "@koshkodrop/shared";

import { haptic } from "../../lib/telegram";

import { RARITY_COLOR, RARITY_INTENSITY, RARITY_LABEL } from "./rarity";

interface Props {
  opening: CaseOpeningDto;
  extras?: CaseOpeningDto[];
  canRepeat: boolean;
  onRepeat: () => void;
  onClose: () => void;
}

export function PrizeOverlay({ opening, extras = [], canRepeat, onRepeat, onClose }: Props) {
  const color = RARITY_COLOR[opening.item.rarity];
  const pack = extras.length > 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-night-950/80 px-6 backdrop-blur-md">
      <div
        className="animate-prize-in relative w-full max-w-[330px] overflow-hidden rounded-[30px] border p-6 text-center"
        style={{
          borderColor: `color-mix(in oklab, ${color} 55%, transparent)`,
          background: `linear-gradient(180deg, color-mix(in oklab, ${color} 16%, #13112f), #0a0920)`,
          boxShadow: `0 24px 60px color-mix(in oklab, ${color} 30%, transparent)`,
        }}
      >
        <div
          className="animate-rays pointer-events-none absolute left-1/2 top-[86px] size-[420px] -translate-x-1/2 -translate-y-1/2"
          style={{
            opacity: RARITY_INTENSITY[opening.item.rarity],
            background: `repeating-conic-gradient(from 0deg, ${color} 0deg 7deg, transparent 7deg 20deg)`,
            maskImage: "radial-gradient(circle, black 8%, transparent 62%)",
          }}
        />

        <p className="relative text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/40">
          {opening.caseTitle}
        </p>

        <div className="relative mt-4 flex justify-center">
          <span
            className="text-[72px] leading-none"
            style={{ filter: `drop-shadow(0 10px 26px color-mix(in oklab, ${color} 70%, transparent))` }}
          >
            {opening.item.emoji}
          </span>
        </div>

        <p className="relative mt-3 text-xl font-extrabold text-white">{opening.item.name}</p>
        <p
          className="relative mt-1.5 inline-block rounded-lg px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide"
          style={{ background: `color-mix(in oklab, ${color} 22%, transparent)`, color }}
        >
          {RARITY_LABEL[opening.item.rarity]}
        </p>

        <p className="tabular relative mt-4 text-3xl font-extrabold" style={{ color }}>
          {formatPaws(BigInt(opening.payout))} 🐾
        </p>
        {pack && (
          <div className="relative mt-4 flex flex-wrap justify-center gap-2">
            {extras.map((row) => (
              <span key={row.id} className="text-2xl" title={row.item.name}>
                {row.item.emoji}
              </span>
            ))}
          </div>
        )}
        <p className="relative mt-1.5 text-xs font-bold leading-relaxed text-white/40">
          Предмет в инвентаре. Активируйте его, чтобы получить лапки.
        </p>

        <div className="relative mt-6 flex gap-2">
          <button
            type="button"
            onClick={() => {
              haptic.select();
              onClose();
            }}
            className="flex-1 rounded-2xl border border-white/12 py-3 text-sm font-bold text-white/70 active:scale-[0.97]"
          >
            Закрыть
          </button>
          <button
            type="button"
            disabled={!canRepeat}
            onClick={() => {
              haptic.tap("medium");
              onRepeat();
            }}
            className="flex-[1.4] rounded-2xl py-3 text-sm font-extrabold text-night-950 active:scale-[0.97] disabled:opacity-40"
            style={{ background: color }}
          >
            Ещё раз
          </button>
        </div>
      </div>
    </div>
  );
}
